import api from '../../services/api';
import { downloadBlob, ensurePdfBlob, parseContentDispositionFilename } from '../../utils/downloadBlob';

export const getHallDashboard = async (params = {}) => {
  const response = await api.get('/hall/api/dashboard/', { params });
  return response.data;
};

export const getHalls = async (params = {}) => {
  const response = await api.get('/hall/api/halls/', { params });
  return response.data;
};

export const createHall = async (payload) => {
  const isFormData = typeof FormData !== 'undefined' && payload instanceof FormData;
  const response = await api.post('/hall/api/halls/', payload, isFormData
    ? { headers: { 'Content-Type': 'multipart/form-data' } }
    : undefined);
  return response.data;
};

export const getHallDetail = async (hallId, params = {}) => {
  const response = await api.get(`/hall/api/halls/${hallId}/`, { params });
  return response.data;
};

export const updateHall = async (hallId, payload) => {
  const isFormData = typeof FormData !== 'undefined' && payload instanceof FormData;
  const response = await api.put(`/hall/api/halls/${hallId}/`, payload, isFormData
    ? { headers: { 'Content-Type': 'multipart/form-data' } }
    : undefined);
  return response.data;
};

export const deleteHall = async (hallId) => {
  await api.delete(`/hall/api/halls/${hallId}/`);
};

export const getHallBookings = async (params = {}) => {
  const response = await api.get('/hall/api/bookings/', { params });
  return response.data;
};

export const getHallBookingDetail = async (bookingId) => {
  const response = await api.get(`/hall/api/bookings/${bookingId}/`);
  return response.data;
};

export const updateHallBooking = async (bookingId, payload) => {
  const response = await api.put(`/hall/api/bookings/${bookingId}/`, payload);
  return response.data;
};

export const deleteHallBooking = async (bookingId) => {
  await api.delete(`/hall/api/bookings/${bookingId}/`);
};

export const getHallVendors = async (params = {}, config = {}) => {
  const response = await api.get('/hall/api/vendors/', {
    ...config,
    params: {
      ...(config.params || {}),
      ...params,
    },
  });
  return response.data;
};

export const getHallVendorDetail = async (vendorId, config = {}) => {
  const response = await api.get(`/hall/api/vendors/${vendorId}/`, config);
  return response.data;
};

export const createHallVendor = async (payload) => {
  const response = await api.post('/hall/api/vendors/', payload);
  return response.data;
};

export const updateHallVendor = async (vendorId, payload) => {
  const response = await api.put(`/hall/api/vendors/${vendorId}/`, payload);
  return response.data;
};

export const deleteHallVendor = async (vendorId) => {
  await api.delete(`/hall/api/vendors/${vendorId}/`);
};

export const updateHallBookingVendors = async (bookingId, payload, config = {}) => {
  const response = await api.put(`/hall/api/bookings/${bookingId}/vendors/`, payload, config);
  return response.data;
};

export const createHallBookingVendorAssignment = async (bookingId, payload) => {
  const response = await api.post(`/hall/api/bookings/${bookingId}/vendor-assignments/`, payload);
  return response.data;
};

export const updateHallBookingVendorAssignment = async (bookingId, assignmentId, payload) => {
  const response = await api.patch(
    `/hall/api/bookings/${bookingId}/vendor-assignments/${assignmentId}/`,
    payload,
  );
  return response.data;
};

export const deleteHallBookingVendorAssignment = async (bookingId, assignmentId) => {
  const response = await api.delete(
    `/hall/api/bookings/${bookingId}/vendor-assignments/${assignmentId}/`,
  );
  return response.data;
};

export const addHallBookingVendorPayment = async (bookingId, assignmentId, payload) => {
  const response = await api.post(
    `/hall/api/bookings/${bookingId}/vendor-assignments/${assignmentId}/payments/`,
    payload,
  );
  return response.data;
};

export const removeHallBookingVendorPayment = async (bookingId, assignmentId, paymentId) => {
  const response = await api.delete(
    `/hall/api/bookings/${bookingId}/vendor-assignments/${assignmentId}/payments/${paymentId}/`,
  );
  return response.data;
};

export const createHallBooking = async (payload) => {
  const response = await api.post('/hall/api/bookings/', payload);
  return response.data;
};

export const updateHallBookingFinancials = async (bookingId, payload) => {
  const response = await api.put(`/hall/api/bookings/${bookingId}/financials/`, payload);
  return response.data;
};

export const getHallBookingPayments = async (bookingId) => {
  const response = await api.get(`/hall/api/bookings/${bookingId}/payments/`);
  return response.data;
};

export const addHallBookingPayment = async (bookingId, payload) => {
  const response = await api.post(`/hall/api/bookings/${bookingId}/payments/`, payload);
  return response.data;
};

export const fetchHallBookingConfirmationPdfBlob = async (bookingId) => {
  const response = await api.get(`/hall/api/bookings/${bookingId}/download-confirmation/`, {
    responseType: 'blob',
  });
  return ensurePdfBlob(response.data, 'Failed to load hall confirmation PDF');
};

export const fetchHallBookingInvoicePdfBlob = async (bookingId) => {
  const response = await api.get(`/hall/api/bookings/${bookingId}/download-invoice/`, {
    responseType: 'blob',
  });
  return ensurePdfBlob(response.data, 'Failed to load hall invoice PDF');
};

export const downloadHallBookingConfirmation = async (bookingId) => {
  const response = await api.get(`/hall/api/bookings/${bookingId}/download-confirmation/`, {
    responseType: 'blob',
  });
  const disposition = response.headers['content-disposition'];
  const filename = parseContentDispositionFilename(
    disposition,
    `HallConfirmationVoucher_${bookingId}.pdf`,
  );
  await downloadBlob(response.data, filename);
};

export const downloadHallBookingInvoice = async (bookingId) => {
  const response = await api.get(`/hall/api/bookings/${bookingId}/download-invoice/`, {
    responseType: 'blob',
  });
  const disposition = response.headers['content-disposition'];
  const filename = parseContentDispositionFilename(
    disposition,
    `HallInvoice_${bookingId}.pdf`,
  );
  await downloadBlob(response.data, filename);
};

export const sendHallBookingDocuments = async (bookingId, options = {}) => {
  const response = await api.post(`/hall/api/bookings/${bookingId}/send-documents/`, {
    send_email: options.send_email === true,
    send_whatsapp: options.send_whatsapp === true,
  });
  return response.data;
};
